<template>
  <div class="form-container">
    <div class="form-header">
      <h2>{{ isEditing ? 'Edit Channel' : 'Create New Channel' }}</h2>
    </div>
    <LoadingIndicator v-if="isLoadingChannel">Loading Channel...</LoadingIndicator>
    <form v-else @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>Information</h3>
        <div class="form-group half-width">
          <label for="name" class="required">Name</label>
          <input
            type="text"
            id="name"
            v-model="formData.name"
            class="form-control"
            maxlength="80"
            required
          >
        </div>

        <div v-if="isSuperuser()" class="form-group half-width">
          <label for="organization" class="required">Organization</label>
          <LoadingIndicator v-if="isLoadingOrgs">Loading organizations...</LoadingIndicator>
          <select
            v-else
            id="organization"
            v-model="formData.organization_id"
            class="form-control"
            required
          >
            <option value="" disabled>Select an organization</option>
            <option v-for="org in sortedOrganizations" :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
          </select>
        </div>

        <div class="form-group full-width">
          <label for="banner_url">Banner Image</label>
          <ImageUploadField
            v-model="formData.banner_url"
            folder="distributor_content"
            :placeholder="'Upload banner image'"
          />
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <textarea
            id="description"
            v-model="formData.description"
            class="form-control"
            rows="4"
            placeholder="Max 2000 characters"
          ></textarea>
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" class="submit-button big" :disabled="isSaving">
          {{ isSaving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Channel') }}
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { handleApiError } from '@/scripts/shared-utils';
import ImageUploadField from '@/components/ImageUploadField.vue';
import { isSuperuser } from '@/scripts/permissions';
import { getAllOrganizations, organizationKeys } from '@/scripts/api/organizationApi';
import { getChannel, createChannel, updateChannel, channelKeys } from '@/scripts/api/channelApi';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const channelId = computed(() => route.params.channel_id);
const isEditing = computed(() => !!channelId.value);

const formData = ref({});

const { data: channelData, isLoading: isLoadingChannel } = useQuery({
  queryKey: computed(() => channelKeys.getChannel(channelId.value)),
  queryFn: () => getChannel(channelId.value),
  enabled: isEditing,
  staleTime: 60000,
  onError: (error) => handleApiError(error, 'fetching channel data'),
});

const { data: organizations, isLoading: isLoadingOrgs } = useQuery({
  queryKey: organizationKeys.getAllOrganizations(),
  queryFn: getAllOrganizations,
  enabled: computed(() => isSuperuser()),
  placeholderData: [],
  staleTime: 60000,
  onError: (error) => handleApiError(error, 'fetching organizations'),
});

const sortedOrganizations = computed(() => {
  if (!organizations.value) return [];
  return [...organizations.value].sort((a, b) => a.name.localeCompare(b.name));
});

watch(channelData, (newChannel) => {
  if (newChannel) {
    formData.value = { ...newChannel };
  }
}, { immediate: true });

const { mutate: saveChannel, isPending: isSaving } = useMutation({
  mutationFn: (channelPayload) => {
    if (isEditing.value) {
      return updateChannel(channelId.value, channelPayload);
    }
    return createChannel(channelPayload.organization_id, channelPayload);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: channelKeys.getAllChannels() });
    queryClient.invalidateQueries({ queryKey: organizationKeys.getAllOrganizationsWithDetails() });
    if (isEditing.value) {
      queryClient.invalidateQueries({ queryKey: channelKeys.getChannel(channelId.value) });
    }
    if (formData.value.organization_id) {
      queryClient.invalidateQueries({ 
        queryKey: organizationKeys.getOrganizationChannels(formData.value.organization_id) 
      });
    }
    closeForm();
  },
  onError: (error) => {
    handleApiError(error, 'submitting channel');
    alert('Error saving channel. Please try again.');
  },
});

const handleSubmit = () => {
  saveChannel(formData.value);
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.push(isSuperuser() ? '/admin/channels' : '/');
};

</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
